import React, { useState } from 'react';
import './ProductionLine.css';
import { useAuth } from './AuthContext';
import Login from './Login';

interface MachineRecord {
  activated: boolean;
  ipJotor: string;
  ipCustomer: string;
  ipOfficeOater: string;
}

const STORAGE_KEY = 'productionLineMachines';

const allMachineIds = Array.from({ length: 99 }, (_, i) => `M${String(i + 1).padStart(2, '0')}`);

const emptyRecord = (): MachineRecord => ({
  activated: false,
  ipJotor: '',
  ipCustomer: '',
  ipOfficeOater: ''
});

const loadMachines = (): Record<string, MachineRecord> => {
  const saved = localStorage.getItem(STORAGE_KEY);
  const parsed = saved ? JSON.parse(saved) : {};
  const machines: Record<string, MachineRecord> = {};
  allMachineIds.forEach(id => {
    machines[id] = parsed[id] || emptyRecord();
  });
  return machines;
};

const ProductionLine: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const [machines, setMachines] = useState<Record<string, MachineRecord>>(loadMachines);
  const [editingCell, setEditingCell] = useState<string | null>(null);

  if (!currentUser) {
    return <Login />;
  }

  const persist = (next: Record<string, MachineRecord>) => {
    setMachines(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  const initializeMachine = (machineId: string) => {
    persist({
      ...machines,
      [machineId]: { ...machines[machineId], activated: true }
    });
  };

  const deactivateMachine = (machineId: string) => {
    persist({
      ...machines,
      [machineId]: { ...machines[machineId], activated: false }
    });
  };

  const updateField = (machineId: string, field: keyof Omit<MachineRecord, 'activated'>, value: string) => {
    persist({
      ...machines,
      [machineId]: { ...machines[machineId], [field]: value }
    });
  };

  const EditableIpCell = ({ machineId, field, value }: {
    machineId: string;
    field: keyof Omit<MachineRecord, 'activated'>;
    value: string;
  }) => {
    const cellKey = `${machineId}-${field}`;
    const isEditing = editingCell === cellKey;

    if (isEditing) {
      return (
        <input
          type="text"
          defaultValue={value}
          autoFocus
          onBlur={(e) => {
            updateField(machineId, field, e.target.value);
            setEditingCell(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              updateField(machineId, field, (e.target as HTMLInputElement).value);
              setEditingCell(null);
            }
            if (e.key === 'Escape') setEditingCell(null);
          }}
          className="pl-editable-input"
          placeholder={`Enter ${field}...`}
        />
      );
    }

    return (
      <span
        onClick={() => setEditingCell(cellKey)}
        className={`pl-editable-cell ${!value ? 'pl-empty' : ''}`}
        title="Click to edit"
      >
        {value || 'Not Set'}
      </span>
    );
  };

  const activatedCount = Object.values(machines).filter(m => m.activated).length;

  return (
    <div className="production-line-container">
      <header className="production-line-header">
        <h1>Production Line</h1>
        <div className="pl-stats">
          <span className="pl-stat-card">{activatedCount} / {allMachineIds.length} Activated</span>
          <span className="pl-stat-card">User: {currentUser.email}</span>
        </div>
        <button onClick={logout} className="pl-logout-btn">Logout</button>
      </header>

      <div className="production-line-table">
        <table>
          <thead>
            <tr>
              <th>Machine</th>
              <th>Status</th>
              <th>IP Jotor</th>
              <th>IP at Customer Site</th>
              <th>IP Oater Office</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {allMachineIds.map(machineId => {
              const machine = machines[machineId];
              return (
                <tr key={machineId} className={machine.activated ? 'pl-activated' : 'pl-inactive'}>
                  <td className="pl-machine-id">{machineId}</td>
                  <td>
                    <span className={`pl-status-badge ${machine.activated ? 'pl-status-on' : 'pl-status-off'}`}>
                      {machine.activated ? 'Activated' : 'Not Activated'}
                    </span>
                  </td>
                  <td>
                    {machine.activated
                      ? <EditableIpCell machineId={machineId} field="ipJotor" value={machine.ipJotor} />
                      : <span className="pl-dash">—</span>}
                  </td>
                  <td>
                    {machine.activated
                      ? <EditableIpCell machineId={machineId} field="ipCustomer" value={machine.ipCustomer} />
                      : <span className="pl-dash">—</span>}
                  </td>
                  <td>
                    {machine.activated
                      ? <EditableIpCell machineId={machineId} field="ipOfficeOater" value={machine.ipOfficeOater} />
                      : <span className="pl-dash">—</span>}
                  </td>
                  <td>
                    {!machine.activated ? (
                      <button className="pl-initialize-btn" onClick={() => initializeMachine(machineId)}>
                        Initialize
                      </button>
                    ) : (
                      <button className="pl-deactivate-btn" onClick={() => deactivateMachine(machineId)}>
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductionLine;
